import { isOrderChange, useOrderHistory } from '@/api/hooks/useOrderHistory'
import type { OrderPerspectiveView } from '@/api/hooks/useOrders'
import { useTenantStore } from '@/stores/tenantStore'
import { CloseOutlined } from '@ant-design/icons'
import { Vertical } from '@gravitate-js/excalibrr'
import { Button, Drawer, Empty, Spin, theme } from 'antd'
import { OrderChangeCard } from '../OrderHistoryDrawer/OrderChangeCard'
import { BUTTON_CONTAINER_STYLE } from './styles'

interface OrderChangeRequestDrawerProps {
  order: OrderPerspectiveView | null
  changeId: string | null
  open: boolean
  onClose: () => void
  onAccept: () => void
  onReject: () => void
  isAccepting?: boolean
  isRejecting?: boolean
}

export function OrderChangeRequestDrawer({
  order,
  changeId,
  open,
  onClose,
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
}: OrderChangeRequestDrawerProps) {
  const { token } = theme.useToken()
  const { selectedTenant } = useTenantStore()
  const orderNumber =
    order?.internal_order_number ?? order?.partner_order_number
  const { data: items, isLoading } = useOrderHistory(orderNumber ?? null)

  // Find the specific pending order change by ID
  const pendingChange = items
    ?.filter(isOrderChange)
    .find((item) => item.id === changeId)

  const isProcessing = isAccepting || isRejecting

  const currentTenantName = selectedTenant?.name
  const isDestination =
    currentTenantName != null &&
    pendingChange?.destination_tenant_name === currentTenantName
  const isOrigin =
    currentTenantName != null &&
    pendingChange?.origin_tenant_name === currentTenantName
  const showAccept = isDestination
  const showReject = isDestination || isOrigin

  return (
    <Drawer
      title={`Order ${orderNumber ?? ''} Change Request`}
      placement='right'
      width={720}
      open={open}
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      styles={{
        body: {
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {isLoading ? (
        <Vertical
          className='h-full'
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            flex: 1,
          }}
        >
          <Spin />
          <span style={{ marginTop: 12, color: token.colorTextSecondary }}>
            Loading change request...
          </span>
        </Vertical>
      ) : !pendingChange ? (
        <Empty description='Change request not found' />
      ) : (
        <Vertical style={{ flex: 1 }}>
          {/* Change details - pre-expanded, no timeline */}
          <div style={{ flex: 1 }}>
            <OrderChangeCard
              change={pendingChange}
              defaultExpanded
              hideTimeline
            />
          </div>

          {/* Action buttons */}
          {(showReject || showAccept) && (
            <div style={BUTTON_CONTAINER_STYLE}>
              {showReject && (
                <Button
                  danger
                  onClick={onReject}
                  loading={isRejecting}
                  disabled={isProcessing}
                  style={{ minWidth: 100 }}
                >
                  Reject
                </Button>
              )}
              {showAccept && (
                <Button
                  type='primary'
                  onClick={onAccept}
                  loading={isAccepting}
                  disabled={isProcessing}
                  style={{ minWidth: 100 }}
                >
                  Accept
                </Button>
              )}
            </div>
          )}
        </Vertical>
      )}
    </Drawer>
  )
}
